import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import type { CreateDeviceRequest, UpdateDeviceRequest } from "@opus-dt/shared-types";
import type { DeviceSummary, CreatedDeviceResponse } from "../../types/devices.types.js";

export type { DeviceSummary, CreatedDeviceResponse } from "../../types/devices.types.js";

const QK = ["devices"] as const;

export function useDevices() {
  return useQuery<DeviceSummary[]>({
    queryKey: QK,
    queryFn: () => api.get<DeviceSummary[]>("/api/v1/devices"),
  });
}

export function useDevice(id: string) {
  return useQuery<DeviceSummary>({
    queryKey: [...QK, id],
    queryFn: () => api.get<DeviceSummary>(`/api/v1/devices/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateDeviceRequest) =>
      api.post<CreatedDeviceResponse>("/api/v1/devices", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
    meta: { skipErrorToast: true },
  });
}

export function useUpdateDevice(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateDeviceRequest) =>
      api.patch<DeviceSummary>(`/api/v1/devices/${id}`, data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK });
      void qc.invalidateQueries({ queryKey: [...QK, id] });
    },
    meta: { skipErrorToast: true },
  });
}

export function useRotateDeviceKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<CreatedDeviceResponse>(`/api/v1/devices/${id}/api-key`),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
    meta: { skipErrorToast: true },
  });
}

export function useDeleteDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/devices/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK }),
    meta: { skipErrorToast: true },
  });
}
